import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
  MenuItem,
} from "@mui/material";
import useUpdate from "../../api/useUpdate";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";

const PayrollEdit = ({ open, onClose, editData, token }) => {
  const { t } = useTranslation();
  const { handleUpdate, loading, isSuccess } = useUpdate("shop/payrolls", token);

  // Fetch foreign key data: shops, employees, and currencies.
  const { data: shopsData, loading: shopLoading } = useFetchObjects(["shop"], "shop/shop", token);
  const { data: employeesData, loading: employeeLoading } = useFetchObjects(["employee"], "shop/employees", token);
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(["currency"], "shop/currencies", token);

  const shops = Array.isArray(shopsData) ? shopsData : shopsData?.results || [];
  const employees = Array.isArray(employeesData) ? employeesData : employeesData?.results || [];
  const currencies = Array.isArray(currenciesData) ? currenciesData : currenciesData?.results || [];

  const [selectedShop, setSelectedShop] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [amount, setAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [monthYear, setMonthYear] = useState("");
  const [notes, setNotes] = useState("");

  // Set initial values from editData.
  useEffect(() => {
    if (editData) {
      setSelectedShop(
        editData.shop && editData.shop.id
          ? String(editData.shop.id)
          : editData.shop
          ? String(editData.shop)
          : ""
      );
      setSelectedEmployee(
        editData.employee && editData.employee.id
          ? String(editData.employee.id)
          : editData.employee
          ? String(editData.employee)
          : ""
      );
      setSelectedCurrency(
        editData.currency && editData.currency.id
          ? String(editData.currency.id)
          : editData.currency
          ? String(editData.currency)
          : ""
      );
      setAmount(editData.amount || "");
      const year = editData.year || "";
      const month = editData.month ? String(editData.month).padStart(2, "0") : "";
      setMonthYear(year && month ? `${year}-${month}` : "");
      setPaymentDate(editData.payment_date ? editData.payment_date.substring(0, 16) : "");
      setNotes(editData.notes || "");
    }
  }, [editData]);

  // Validate foreign key selections when data loads.
  useEffect(() => {
    if (editData && shops && shops.length > 0) {
      const shopId =
        editData.shop && editData.shop.id
          ? String(editData.shop.id)
          : editData.shop
          ? String(editData.shop)
          : "";
      const exists = (shops || []).find((s) => String(s.id) === shopId);
      if (exists) {
        setSelectedShop(shopId);
      } else {
        setSelectedShop("");
      }
    }
  }, [editData, shops]);

  useEffect(() => {
    if (editData && employees && employees.length > 0) {
      const employeeId =
        editData.employee && editData.employee.id
          ? String(editData.employee.id)
          : editData.employee
          ? String(editData.employee)
          : "";
      const exists = (employees || []).find((e) => String(e.id) === employeeId);
      if (exists) {
        setSelectedEmployee(employeeId);
      } else {
        setSelectedEmployee("");
      }
    }
  }, [editData, employees]);

  useEffect(() => {
    if (editData && currencies && currencies.length > 0) {
      const currencyId =
        editData.currency && editData.currency.id
          ? String(editData.currency.id)
          : editData.currency
          ? String(editData.currency)
          : "";
      const exists = (currencies || []).find((c) => String(c.id) === currencyId);
      if (exists) {
        setSelectedCurrency(currencyId);
      } else {
        setSelectedCurrency("");
      }
    }
  }, [editData, currencies]);

  const handleSubmit = () => {
    const formData = new FormData();
    formData.append("shop", selectedShop);
    formData.append("employee", selectedEmployee);
    formData.append("currency", selectedCurrency);
    formData.append("amount", amount);
    if (monthYear) {
      const [year, month] = monthYear.split("-");
      formData.append("year", year);
      formData.append("month", month);
    }
    formData.append("payment_date", paymentDate);
    formData.append("notes", notes);
    handleUpdate(editData.id, formData);
  };

  useEffect(() => {
    if (isSuccess) {
      setSelectedShop("");
      setSelectedEmployee("");
      setSelectedCurrency("");
      setAmount("");
      setMonthYear("");
      setPaymentDate("");
      setNotes("");
      onClose();
    }
  }, [isSuccess, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("payroll.editPayroll")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <TextField
            select
            margin="dense"
            label={t("payroll.selectShop")}
            fullWidth
            required
            value={selectedShop}
            onChange={(e) => setSelectedShop(e.target.value)}
          >
            {shopLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              (shops || []).map((shop) => (
                <MenuItem key={shop.id} value={String(shop.id)}>
                  {shop.name}
                </MenuItem>
              ))
            )}
          </TextField>
          <TextField
            select
            margin="dense"
            label={t("payroll.selectEmployee")}
            fullWidth
            required
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
          >
            {employeeLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              (employees || []).map((emp) => (
                <MenuItem key={emp.id} value={String(emp.id)}>
                  {emp.name || "--"}
                </MenuItem>
              ))
            )}
          </TextField>
          <TextField
            select
            margin="dense"
            label={t("payroll.selectCurrency")}
            fullWidth
            required
            value={selectedCurrency}
            onChange={(e) => setSelectedCurrency(e.target.value)}
          >
            {currencyLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              (currencies || []).map((cur) => (
                <MenuItem key={cur.id} value={String(cur.id)}>
                  {cur.title || "--"}
                </MenuItem>
              ))
            )}
          </TextField>
          <TextField
            margin="dense"
            label={t("payroll.amount")}
            type="number"
            fullWidth
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          {editData?.employee_details?.salary && amount && (
            <TextField
              margin="dense"
              label={t("payroll.remainingAmount")}
              type="text"
              fullWidth
              value={(parseFloat(editData.employee_details.salary) - parseFloat(amount)).toFixed(2)}
              InputProps={{
                readOnly: true,
                style: {
                  color: (parseFloat(editData.employee_details.salary) - parseFloat(amount)) < 0 ? 'red' : 'green'
                }
              }}
            />
          )}
          <TextField
            margin="dense"
            label={t("payroll.monthYear", "Month/Year")}
            type="month"
            fullWidth
            required
            value={monthYear}
            onChange={(e) => setMonthYear(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            margin="dense"
            label={t("payroll.paymentDate")}
            type="datetime-local"
            fullWidth
            required
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
          />
          <TextField
            margin="dense"
            label={t("payroll.notes")}
            fullWidth
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("payroll.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading ||
            !selectedShop ||
            !selectedEmployee ||
            !selectedCurrency ||
            !amount ||
            !monthYear ||
            !paymentDate
          }
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("payroll.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PayrollEdit;
